import { useState } from "react";
import { useTranslation } from "react-i18next";
import { whyGoIconKey, whyGoItemSchema, type WhyGoItem } from "@majourneys/shared";
import { pewterFrame, pewterInner, solid } from "@majourneys/shared/ui";
import { PW, pewterPanelApproved } from "../../internal-ui/pewter-shell";

/**
 * "Why people go": a restaurant's three reasons on the soft pewter panel,
 * each with its emoji in a small pewter frame (owner, 2026-09-28, the pewter
 * version from /restaurant-why-lab). Reads `contentBlocks.whyGo`, the
 * visitor's locale first, then English. Renders nothing when the venue has
 * none, so the page falls back to its fact tiles.
 *
 * The emoji is drawn as its 3D icon when one exists (owner, 2026-10-25; table
 * in `whyGoIconKey`), and as the emoji itself otherwise.
 */

const LOCALES = ["en", "fr", "ar", "es", "it", "de", "nl"] as const;
type Locale = (typeof LOCALES)[number];

const TILE_RADIUS = 16;
const TILE_GAP = 3;

export function readWhyGo(blocks: Record<string, unknown> | null | undefined, lang: string): WhyGoItem[] {
  const block = blocks?.whyGo as Partial<Record<Locale, unknown>> | undefined;
  if (!block || typeof block !== "object") return [];
  const base = (lang ?? "en").split("-")[0] as Locale;
  const locale: Locale = LOCALES.includes(base) ? base : "en";
  const pick = (raw: unknown): WhyGoItem[] =>
    Array.isArray(raw)
      ? raw.flatMap((it) => {
          const parsed = whyGoItemSchema.safeParse(it);
          return parsed.success ? [parsed.data] : [];
        })
      : [];
  const local = pick(block[locale]);
  return local.length > 0 ? local : pick(block.en);
}

export function WhyGoPanel({ blocks }: { blocks: Record<string, unknown> | null | undefined }) {
  const { t, i18n } = useTranslation();
  const items = readWhyGo(blocks, i18n.language);
  if (items.length === 0) return null;

  return (
    <section aria-label={t("blocks.why_go")}>
      <ul className="px-5 py-1" style={pewterPanelApproved(26)}>
        {items.map((it, k) => (
          <li
            key={`${k}-${it.title}`}
            className="flex items-start gap-4 py-5"
            style={{ borderTop: k === 0 ? "none" : `1px solid ${PW.hairline}` }}
          >
            <span aria-hidden="true" className="h-12 w-12 shrink-0" style={pewterFrame(TILE_RADIUS, { gap: TILE_GAP })}>
              <span
                className="flex h-full w-full items-center justify-center text-[23px]"
                style={pewterInner(solid("#F3F3F8"), TILE_RADIUS, { gap: TILE_GAP })}
              >
                <WhyGoIcon emoji={it.emoji} />
              </span>
            </span>
            <div className="min-w-0">
              <h3 className="font-sans text-[16px] font-semibold leading-snug text-[#110E39]">{it.title}</h3>
              <p className="mt-0.5 font-sans text-[15px] leading-[1.5] text-[#5a5d77]">{it.line}</p>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * A site without the icon files (the city-guide apps share this package) gets
 * a 404, so a failed load falls back to the emoji rather than a broken image.
 */
function WhyGoIcon({ emoji }: { emoji: string }) {
  const key = whyGoIconKey(emoji);
  const [failed, setFailed] = useState(false);
  if (!key || failed) return <>{emoji}</>;
  return (
    <img
      src={`/images/why-go/${key}.webp`}
      alt=""
      width={30}
      height={30}
      loading="lazy"
      decoding="async"
      data-testid="why-go-icon"
      className="h-[30px] w-[30px] object-contain"
      onError={() => setFailed(true)}
    />
  );
}
